import { createContext } from "react";
import { createTheme } from "@mui/material/styles";

const generateTheme = (fontSize, mode) => {
  return createTheme({
    palette: {
      mode,

      ...(mode === "light"
        ? {
          primary: {
            main: "#006633",
            interactive: "#006633",
          },
          button: {
            primaryBackground: "rgba(247, 213, 8, 1)",
            primaryColor: "rgba(28, 33, 30, 1)",
            primaryHoverBackground: "rgba(133, 188, 49, 1)",
            secondaryBackground: "rgba(255, 255, 255, 1)",
            secondaryColor: "rgba(28, 33, 30, 1)",
            secondaryBorder: "rgba(162, 171, 166, 1)",
            secondaryHoverBackground: "rgba(133, 188, 49, 0.3)",
            secondaryHoverColor: "rgba(28, 33, 30, 1)",
            disabledBackground: "rgba(96, 108, 102, 1)",
            disabledColor: "rgba(255, 255, 255, 1)",
          },
          background: {
            default: "#ffffff",
            contactCardGradient:
              "linear-gradient(270deg, #FFFFFF 0%, rgba(219, 239, 188, 0.8) 100%)",
            primaryGreen: "rgba(26, 92, 75, 1)",
            tertiaryGreen: "rgba(240, 245, 242, 1)",
            white: "#fff",
            tertiaryWhite: "#fff",
          },
          icon: {
            faqIcon: "rgba(240, 245, 242, 1)",
          },
          text: {
            primary: "rgba(28, 33, 30, 1)",
            white: "#ffffff",
            secondary: "rgba(26, 92, 75, 1)",
            textGrey: "rgba(92, 100, 96, 1)",
            textGreen: "rgba(26, 92, 75, 1)",
            yellow: "rgba(247, 213, 8, 1)",
            greyGreen: "rgba(240, 245, 242, 1)",
            darkGreyGreen: "rgba(61, 69, 65, 1)",
            newsDate: "rgb(111,120,115)",
            error: "rgba(226, 41, 42, 1)",
            success: "rgba(133, 188, 49, 1)",
            warning: "rgba(238, 116, 30, 1)",
          },
          aqi: {
            surface: "#F3F6F6",
            text: "#7A8A8A",
            heading: "#030808",
            divider: "#E0E0E0",
            value: "#033006",
          },
          chart: {
            // Brand color hierarchy for charts & graphs — primary palette first,
            // then secondary tints (per the brand color-hierarchy guidelines).
            colors: [
              "#284181", // 1 navy (primary)
              "#00bfdf", // 2 cyan (primary)
              "#f79433", // 3 orange (primary)
              "#5a5a5a", // 4 charcoal (primary)
              "#436ab3", // 5 medium blue (secondary)
              "#6dcff6", // 6 light blue (secondary tint)
              "#fdb913", // 7 gold (secondary)
              "#939598", // 8 gray (secondary)
              "#d1d3d4", // 9 light gray (tint)
              "#e6e7e8", // 10 lightest gray (tint)
            ],
            heatMapMin: "#e6e7e8",
            heatMapMax: "#284181",
          },
        }
        : {
          primary: {
            main: "#00e170",
            interactive: "#00e170",
          },
          button: {
            primaryBackground: "rgba(133, 188, 49, 1)",
            primaryColor: "rgba(26, 29, 33, 1)",
            primaryHoverBackground: "rgba(247, 213, 8, 1)",
            secondaryBackground: "rgba(28, 33, 30, 1)",
            secondaryColor: "rgba(133, 188, 49, 1)",
            secondaryBorder: "rgba(133, 188, 49, 1)",
            secondaryHoverBackground: "rgba(255, 255, 255, 1)",
            secondaryHoverColor: "rgba(28, 33, 30, 1)",
            disabledBackground: "rgba(162, 171, 166, 1)",
            disabledColor: "rgba(255, 255, 255, 1)",
          },
          background: {
            default: "rgba(26, 29, 33, 1)",
            primaryGreen: "rgba(133, 188, 49, 1)",
            tertiaryGreen: "rgba(34, 37, 41, 1)",
            white: "#1a1d21",
            tertiaryWhite: "rgba(34, 37, 41, 1)",
          },
          icon: {
            faqIcon: "rgba(133, 188, 49, 1)",
          },
          text: {
            primary: "#ffffff",
            white: "#000000",
            secondary: "rgba(255, 255, 255, 1)",
            textGrey: "rgba(255, 255, 255, 1)",
            textGreen: "rgba(133, 188, 49, 1)",
            yellow: "rgba(133, 188, 49, 1)",
            greyGreen: "rgba(133, 188, 49, 1)",
            darkGreyGreen: "rgba(133, 188, 49, 1)",
            newsDate: "rgba(133, 188, 49, 1)",
            disabled: "rgba(255, 255, 255, 1)",
            error: "rgba(226, 41, 42, 1)",
            success: "rgba(133, 188, 49, 1)",
            warning: "rgba(238, 116, 30, 1)",
          },
          aqi: {
            surface: "rgba(34, 37, 41, 1)",
            text: "rgba(255, 255, 255, 0.7)",
            heading: "#ffffff",
            divider: "rgba(255, 255, 255, 0.12)",
            value: "rgba(133, 188, 49, 1)",
          },
          chart: {
            // Chart palette is theme-independent: dark mode uses the SAME colors
            // as light (light is the single source of truth for the chart color
            // policy). Keep in sync with the light `chart.colors` above.
            colors: [
              "#284181", // 1 navy (primary)
              "#00bfdf", // 2 cyan (primary)
              "#f79433", // 3 orange (primary)
              "#5a5a5a", // 4 charcoal (primary)
              "#436ab3", // 5 medium blue (secondary)
              "#6dcff6", // 6 light blue (secondary tint)
              "#fdb913", // 7 gold (secondary)
              "#939598", // 8 gray (secondary)
              "#d1d3d4", // 9 light gray (tint)
              "#e6e7e8", // 10 lightest gray (tint)
            ],
            heatMapMin: "#e6e7e8",
            heatMapMax: "#284181",
          },
        }),
    },

    typography: {
      // Global default font settings
      fontFamily: "Inter, sans-serif",
      fontSize: 14,
      fontWeightLight: 300,
      fontWeightRegular: 400,
      fontWeightMedium: 500,
      fontWeightBold: 700,

      // Customizing header variants
      h0: {
        fontSize: "5rem",
        fontWeight: 700,
        lineHeight: 1.2,
      },
      h1: {
        fontSize: "2.25rem",
        fontWeight: 600,
        lineHeight: 1.2,
      },
      h2: {
        fontSize: "2rem",
        fontWeight: 600,
        lineHeight: 1.3,
      },
      h3: {
        fontSize: "1.75rem",
        fontWeight: 500,
      },
      h4: {
        fontSize: "1.5rem",
        fontWeight: 400,
        "@media (max-width: 640px)": {
          fontSize: "1.25rem",
        }
      },
      h5: {
        fontSize: "1.25rem",
        fontWeight: 400,
      },
      h6: {
        fontSize: "1rem",
        fontWeight: 400,
      },

      // Customizing subtitle variants
      subtitle1: {
        fontSize: "1.125rem",
        fontWeight: 400,
      },
      subtitle2: {
        fontSize: "1rem",
        fontWeight: 500,
      },

      // Customizing body variants
      body1: {
        fontSize: "1rem",
        fontWeight: 400,
        lineHeight: 1.5,
      },
      body2: {
        fontSize: "1rem",
        fontWeight: 400,
        lineHeight: 1.43,
      },

      // Customizing button text
      button: {
        textTransform: "uppercase",
        fontWeight: 500,
      },

      // Customizing caption text
      caption: {
        fontSize: "0.75rem",
        fontWeight: 400,
        lineHeight: 1.66,
      },
      h7: {
        fontSize: ".875rem",
        fontWeight: 400,
        fontFamily: "Inter, sans-serif",
      },
      // Customizing overline text
      overline: {
        fontSize: "0.75rem",
        fontWeight: 400,
        textTransform: "uppercase",
        lineHeight: 2.66,
      },
      // Chip text
      chip: {
        fontSize: "0.875rem",
        fontWeight: 600,
        lineHeight: "114.286%",
        fontFamily: "Inter, sans-serif",
      }
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            fontSize: "1rem",
            fontWeight: "600 !important",
            textTransform: "none",
            lineHeight: "120%",
            "&.MuiButton-sizeSmall": {
              borderRadius: "4px",
              fontSize: "1.125rem",
              padding: "0.2rem 1rem",
              "@media (max-width:640px)": {
                fontSize: "1rem",
              },
            },
            "&.MuiButton-sizeMedium": {
              borderRadius: "6px",
              fontSize: "1.125rem",
              padding: "0.5rem 1rem",
              "@media (max-width:640px)": {
                fontSize: "1rem",
              },
            },
            "&.MuiButton-sizeLarge": {
              borderRadius: "8px",
              fontSize: "1.125rem",
              padding: "0.75rem 2.5rem",
              "@media (max-width:640px)": {
                fontSize: "1rem",
              },
            },
          },
        },
        variants: [
          {
            props: { variant: "primary" },
            style: ({ theme }) => ({
              fontWeight: 600,
              backgroundColor: theme.palette.button.primaryBackground,
              color: theme.palette.button.primaryColor,
              "&:hover": {
                backgroundColor: theme.palette.button.primaryHoverBackground,
              },
              "&.Mui-disabled": {
                backgroundColor: theme.palette.button.disabledBackground,
                color: theme.palette.button.disabledColor,
              },
            }),
          },
          {
            props: { variant: "secondary" },
            style: ({ theme }) => ({
              backgroundColor: theme.palette.button.secondaryBackground,
              border: `1px solid ${theme.palette.button.secondaryBorder}`,
              color: theme.palette.button.secondaryColor,
              "&:hover": {
                backgroundColor: theme.palette.button.secondaryHoverBackground,
                color: theme.palette.button.secondaryHoverColor,
                border: `1px solid ${theme.palette.button.secondaryHoverBackground}`,
              },

              // MOBILE VIEW ONLY
              "@media (hover: none) and (pointer: coarse)": {
                "&:active": {
                  backgroundColor: theme.palette.button.secondaryBackground,
                  color: theme.palette.button.secondaryColor,
                  border: `1px solid ${theme.palette.button.secondaryColor}`,
                },
                "&:focus": {
                  backgroundColor: theme.palette.button.secondaryBackground,
                  color: theme.palette.button.secondaryColor,
                  border: `1px solid ${theme.palette.button.secondaryColor}`,
                },
                "&:focus-visible": {
                  backgroundColor: theme.palette.button.secondaryBackground,
                  color: theme.palette.button.secondaryColor,
                  border: `1px solid ${theme.palette.button.secondaryColor}`,
                },
              },

              "&.Mui-disabled": {
                backgroundColor: theme.palette.button.disabledBackground,
                color: theme.palette.button.disabledColor,
              },
            }),
          },

          {
            props: { variant: "tertiary" },
            style: ({ theme }) => ({
              border: `none`,
              color: "rgba(92, 100, 96, 1)",
              borderRadius: "0 !important",
              "&.Mui-disabled": {
                backgroundColor: theme.palette.button.disabledBackground,
                color: theme.palette.button.disabledColor,
              },
            }),
          },
          {
            props: { variant: "textButtonBlack" },
            style: ({ theme }) => ({
              border: `none`,
              color: theme.palette.button.secondaryColor,
              borderRadius: "0 !important",
              "&.Mui-disabled": {
                color: theme.palette.button.disabledBackground,
              },
            }),
          },
          {
            props: { variant: "textButton" },
            style: ({ theme }) => ({
              border: `none`,
              color: theme.palette.button.primary,
              borderRadius: "0 !important",
              "&.Mui-disabled": {
                // backgroundColor: theme.palette.button.disabledBackground,
                color: theme.palette.button.disabledColor,
              },
            }),
          },
        ],
      },
      MuiMenu: {
        styleOverrides: {
          paper: ({ theme }) => ({
            backgroundColor: theme.palette.background.tertiaryGreen,
          }),
        },
      },
      MuiContainer: {
        styleOverrides: {
          root: {
            textTransform: "none",
            "&.MuiButton-sizeSmall": {
              borderRadius: "4px",
            },
            "&.MuiButton-sizeMedium": {
              borderRadius: "6px",
            },
            "&.MuiButton-sizeLarge": {
              borderRadius: "8px",
            },
          },
        },
        variants: [
          {
            props: { variant: "primary" },
            style: ({ theme }) => ({
              backgroundColor:
                theme.palette.mode === "dark"
                  ? "rgba(26, 29, 33, 1) !important"
                  : "none",
              width: "100%",
              maxWidth: "100% !important",

              // backgroundImage:
              //   theme.palette.mode === "dark"
              //     ? "rgba(26, 29, 33, 1)"
              //     : "linear-gradient(270deg, #FFFFFF 0%, rgba(219, 239, 188, 0.8) 100%)",
              // boxShadow: theme.shadows[2],
              borderRadius: 0,
              color: theme.palette.text.primary,
              padding: "0px !important",
            }),
          },
          {
            props: { variant: "white" },
            style: ({ theme }) => ({
              backgroundColor: theme.palette.background.white,
              width: "100%",
              maxWidth: "100% !important",
              borderRadius: 0,
              color: theme.palette.text.primary,
              padding: "0px !important",
            }),
          },
          {
            props: { variant: "tertiaryWhite" },
            style: ({ theme }) => ({
              backgroundColor: theme.palette.background.tertiaryWhite,
              width: "100%",
              maxWidth: "100% !important",
              borderRadius: 0,
              color: theme.palette.text.primary,
              padding: "0px !important",
            }),
          },
          {
            props: { variant: "primaryGreen" },
            style: ({ theme }) => ({
              backgroundColor: theme.palette.background.primaryGreen,
              width: "100%",
              maxWidth: "none !important",
              borderRadius: 0,
              color: theme.palette.text.white,
              padding: "0px",
            }),
          },
          {
            props: { variant: "tertiaryGreen" },
            style: ({ theme }) => ({
              backgroundColor: theme.palette.background.tertiaryGreen,
              width: "100%",
              maxWidth: "none !important",
              borderRadius: 0,
              color: theme.palette.text.primary,
              padding: "0px",
            }),
          },
          {
            props: { variant: "gradient" },
            style: ({ theme }) => ({
              backgroundColor:
                theme.palette.mode === "dark"
                  ? "rgba(26, 29, 33, 1) !important"
                  : "none",
              width: "100%",
              maxWidth: "100% !important",

              background:
                theme.palette.mode === "dark"
                  ? "rgba(26, 29, 33, 1)"
                  : "linear-gradient(90deg,rgba(115, 232, 148, 0.3) 0%,rgba(26, 92, 75, 0.2) 100%),#18342d",
              // boxShadow: theme.shadows[2],
              borderRadius: 0,
              color: theme.palette.text.primary,
              padding: "0px !important",
            }),
          },
        ],
      },
      MuiCard: {
        variants: [
          {
            props: { variant: "customCard" },
            style: ({ theme }) => ({
              backgroundColor: "transparent",
              backgroundImage:
                theme.palette.mode === "dark"
                  ? "none"
                  : "linear-gradient(270deg, #FFFFFF 0%, rgba(219, 239, 188, 0.8) 100%)",
              // boxShadow: theme.shadows[2],
              borderRadius: "8px",
              border:
                theme.palette.mode === "dark" ? `1px solid white` : "1px solid rgba(208, 241, 158, 1)",
              padding: theme.spacing(2),
              color: theme.palette.text.primary,
            }),
          },
          {
            props: { variant: "opacityCard" },
            style: ({ theme }) => ({
              backgroundColor:
                theme.palette.mode === "dark"
                  ? "rgba(34, 37, 41, 1)"
                  : "rgba(255, 255, 255, 0.15)",
              // backgroundImage:
              //   theme.palette.mode === "dark"
              //     ? "none"
              //     : "linear-gradient(270deg, #FFFFFF 0%, rgba(219, 239, 188, 0.8) 100%)",
              // boxShadow: theme.shadows[2],
              borderRadius: "8px",

              padding: theme.spacing(2),
              color: "#fff",
            }),
          },
          {
            props: { variant: "white" },
            style: ({ theme }) => ({
              backgroundColor: "white",

              borderRadius: "8px",
              padding: theme.spacing(2),
              color: "black",
            }),
          },
        ],
      },
      MuiAccordion: {
        styleOverrides: {
          root: {
            margin: 0,
            "&.Mui-expanded": {
              margin: 0,
            },
          },
        },
      },
      MuiTypography: {
        variants: [
          {
            props: { variant: "primary" },
            style: ({ theme }) => ({
              color: theme.palette.text.primary,
            }),
          },
          {
            props: { variant: "error" },
            style: ({ theme }) => ({
              color: theme.palette.text.error,
            }),
          },
          {
            props: { variant: "primary-green" },
            style: ({ theme }) => ({
              color: theme.palette.text.textGreen,
            }),
          },
        ],
      },
      MuiTextField: {
        styleOverrides: {
          root: ({ theme }) => ({
            "& input:-webkit-autofill, textarea:-webkit-autofill, select:-webkit-autofill":
            {
              "-webkit-box-shadow": "none", // Removes any autofill background changes
              "-webkit-text-fill-color": theme.palette.text.primary, // Keep text color adjustment
              color: theme.palette.text.primary,
              transition:
                "background-color 5000s ease-in-out 0s, color 5000s ease-in-out 0s",
            },
            "& .MuiOutlinedInput-root": {
              minHeight: "40px",
              "&::before": {
                content: '""',
                // position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "transparent",
                borderRadius: "4px",
                zIndex: 0,
              },
              "&.Mui-disabled ::before": {
                backgroundColor: "rgba(0, 0, 0, 0.1)",
              },
              "& fieldset": {
                borderColor: theme.palette.grey[400],
                borderRadius: "4px",
              },
              "&:hover fieldset": {
                borderColor:
                  theme.palette.mode === "dark" ? "#85BC31" : "#2f6a31",
              },
              "&.Mui-focused fieldset": {
                borderColor:
                  theme.palette.mode === "dark" ? "#85BC31" : "#2f6a31",
              },
              "&.Mui-error fieldset": {
                borderColor: "#F13005 !important",
              },
              "&.Mui-disabled input": {
                cursor: "not-allowed",
                color: "rgba(0, 0, 0, 0.6)",
              },
              "&.Mui-disabled": {
                color: theme.palette.text.disabled,
                "-webkit-text-fill-color": theme.palette.text.disabled,
                cursor: "not-allowed",
                "& fieldset": {
                  borderColor: theme.palette.action.disabled,
                },
              },
            },
          }),
        },
      },
      // App-wide placeholder color for every InputBase-based control
      // (TextField/Select/Autocomplete, and custom `styled(TextField)` /
      // `styled(InputBase)` wrappers like CssTextField). Overriding
      // InputBase's own `input` slot merges into its own style computation
      // instead of fighting it as a separate, later-losing CSS rule, so
      // (unlike the `.Mui-error fieldset` override above) this needs no
      // `!important` — see rewampTheme.js's identical MuiInputBase override
      // for the rewamp-themed half of the app.
      MuiInputBase: {
        styleOverrides: {
          input: ({ theme }) => {
            const placeholderColor = theme.palette.mode === "dark" ? "#8A938D" : "#868686";
            // `-webkit-text-fill-color` paints text in WebKit/Blink and wins
            // over `color`; it's inherited, so a wrapper pinning it on the
            // base `input` (CssTextField) leaks it onto `::placeholder` too
            // unless reset here — see rewampTheme.js's identical override.
            const placeholder = {
              color: placeholderColor,
              WebkitTextFillColor: placeholderColor,
              opacity: 1,
            };
            return {
              "&::-webkit-input-placeholder": placeholder,
              "&::-moz-placeholder": placeholder,
              "&:-ms-input-placeholder": placeholder,
              "&::-ms-input-placeholder": placeholder,
              "&::placeholder": placeholder,
            };
          },
        },
      },
      MuiAutocomplete: {
        styleOverrides: {
          popupIndicator: {
            paddingRight: "7px", // Moves it inward
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          root: ({ theme }) => ({
            minHeight: "auto", // Remove default min-height if needed
          }),
          indicator: ({ theme }) => ({
            backgroundColor: theme.palette.text.textGreen,
            width: "auto",
            height: "3px",
          }),
        },
      },
      MuiTab: {
        styleOverrides: {
          root: ({ theme }) => ({
            textTransform: "none", // Use your typography setting
            fontWeight: theme.typography.fontWeightMedium,
            minWidth: 0,
            padding: theme.spacing(1, 2),
            // Ensure tabs have a smooth transition when selected
            transition: "color 0.3s ease-in-out",
            "&.Mui-selected": {
              color: theme.palette.text.textGreen,
              fontWeight: theme.typography.fontWeightBold,
            },
            // Adjust focus styles if needed
            "&:focus": {
              color: theme.palette.button.primaryHoverBackground,
            },
          }),
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: ({ theme }) => ({
            "&.MuiTableRow-hover:hover": {
              backgroundColor: "rgba(219, 239, 188, 0.4)", // light green for dark mode
            },
          }),
        },
      },
    },
  });
};

export default generateTheme;

export const ThemeContext = createContext();
